import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Card from "components/card";
import { FaRegClock, FaProjectDiagram, FaExclamationCircle } from "react-icons/fa";
import Loader from "components/loaders/Loader";

const ChartSection = ({ approvals = [], isLoading, onClear, viewAllPath = "/admin/manage-team" }) => {
  const navigate = useNavigate();

  const { top, restCount } = useMemo(() => {
    if (!approvals || approvals.length === 0) {
      return { top: null, restCount: 0 };
    }
    const topItem = approvals[0];
    return { top: topItem, restCount: Math.max(approvals.length - 1, 0) };
  }, [approvals]);

  const handleApprovalClick = (approval) => {
    // Navigate and pass the ID to highlight in the navigation state.
    navigate("/admin/manage-team", {
      state: {
        highlightId: approval.id,
        scrollToQueue: true,
      },
    });
  };

  const handleViewAll = () => {
    navigate(viewAllPath, { state: { scrollToTop: true } });
  };

  if (isLoading) {
    return (
      <Card extra="p-6 bg-white dark:bg-navy-800 shadow-lg rounded-[20px] flex items-center justify-center min-h-[250px]">
        <Loader />
      </Card>
    );
  }

  return (
    <Card extra="p-6 bg-white dark:bg-navy-800 shadow-lg rounded-[20px]">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-navy-700 dark:text-white flex items-center">
          <FaExclamationCircle className="text-orange-500 mr-3" />
          Pending Approvals
        </h3>
        <button
          className="px-4 py-2 bg-[#FF6600] text-white rounded-lg shadow-md hover:bg-[#e65c00] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onClear}
          disabled={approvals.length === 0}
        >
          Clear All
        </button>
      </div>

      {!top ? (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          All caught up! No pending approvals at the moment.
        </p>
      ) : (
        <>
          <div
            className="flex items-start p-5 dark:bg-navy-700 bg-gray-50 rounded-lg shadow-sm border-l-4 border-transparent hover:border-brand-900 hover:bg-gray-100 dark:hover:bg-navy-500 transition-all duration-300 cursor-pointer"
            onClick={() => handleApprovalClick(top)}
            title="View Details"
          >
            <div className="mr-4 flex-shrink-0">
              <div className="p-3 bg-blue-100 dark:bg-blue-800 rounded-full">
                <FaProjectDiagram className="text-blue-500 dark:text-blue-400 text-lg" />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                <h4 className="text-lg font-semibold text-navy-700 dark:text-white truncate" title={top.step.poapTitle}>
                  {top.step.poapTitle}
                </h4>
                <div className="flex items-center text-gray-500 dark:text-gray-400 text-xs flex-shrink-0">
                  <FaRegClock className="mr-1" />
                  {moment(top.createdAt).fromNow()}
                </div>
              </div>

              <div className="mb-2">
                <span className="inline-block text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 px-2 py-1 rounded">
                  Most recent
                </span>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2" title={top.step.description}>
                {top.step.description}
              </p>

              <div className="text-sm font-semibold text-navy-700 dark:text-white mb-1">
                Team:{" "}
                <span className="font-bold text-blue-600 dark:text-blue-400">
                  {top.team.name}
                </span>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                Click to review and approve this milestone.
              </p>
            </div>
          </div>

          {restCount > 0 && (
            <div className="mt-4">
              <button
                onClick={handleViewAll}
                className="w-full text-sm font-semibold text-white bg-[#FF6600] hover:bg-[#e65c00] rounded-lg py-2 transition-colors shadow-md">
                View all ({approvals.length})
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default ChartSection;